// Copyright 2025, University of Maryland, All Rights Reserved
import { axiosInstance, sanitizeResponseData } from './client';

export interface AnalysisSummary {
  analysis_id: string;
  status: string;
  created_at?: string;
  completed_at?: string;
  failed_at?: string;
  message?: string;
  document_ids: string[];
  document_count: number;
  filenames?: string[];  // Array of document filenames
  result_size_mb?: string;
  has_inline_data: boolean;
  s3_key?: string;
  error?: string;
  llm_cost?: string;
  prompt_name?: string;
  analysis_type?: string;
  prompt_type?: string;
  template_name?: string;
  template_type?: string;
  created_by?: string;
}

export interface AnalysisListResponse {
  analyses: AnalysisSummary[];
  count: number;
  org_id: string;
  ws_id: string;
  has_more: boolean;
  next_token?: string;
}

export interface AnalysisDetail {
  analysis_id: string;
  org_id: string;
  ws_id: string;
  status: string;
  created_at?: string;
  completed_at?: string;
  failed_at?: string;
  message?: string;
  error?: string;
  document_ids: string[];
  document_count: number;
  result_size_mb?: string;
  s3_key?: string;
  analysis_data?: any;
  data_source?: string;
  presigned_url?: string;
  presigned_url_expires_in?: string;
  s3_error?: string;
}

export const analysisHistoryApi = {
  // List all analyses for a workspace (fetches all pages automatically)
  async listAnalyses(
    org_id: string,
    ws_id: string,
    limit: number = 100,
    status?: string,
    analysis_type?: string
  ): Promise<AnalysisListResponse> {
    try {
      const allAnalyses: AnalysisSummary[] = [];
      let nextToken: string | undefined = undefined;
      let hasMore = true;

      // Fetch all pages
      while (hasMore) {
        const params: Record<string, any> = {
          limit
        };

        if (status) {
          params.status = status;
        }

        if (analysis_type) {
          params.analysis_type = analysis_type;
        }

        if (nextToken) {
          params.next_token = nextToken;
        }

        const response = await axiosInstance.get(`/analysis-history/org/${org_id}/ws/${ws_id}`, {
          params,
          timeout: 30000,
        });

        const data = sanitizeResponseData(response.data) as AnalysisListResponse;
        
        // Add analyses from this page
        allAnalyses.push(...data.analyses);

        // Check if there are more pages
        hasMore = data.has_more && !!data.next_token;
        nextToken = data.next_token;

        console.log(`Fetched ${data.analyses.length} analyses, total so far: ${allAnalyses.length}, has_more: ${hasMore}`);
      }

      // Return combined results
      return {
        analyses: allAnalyses,
        count: allAnalyses.length,
        org_id,
        ws_id,
        has_more: false  // We fetched everything
      };
    } catch (error: any) {
      console.error('List analyses error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to list analyses');
    }
  },

  // Get detailed analysis information
  async getAnalysis(
    org_id: string,
    ws_id: string,
    analysis_id: string,
    include_results: boolean = true
  ): Promise<AnalysisDetail> {
    try {
      const response = await axiosInstance.get(
        `/analysis-history/org/${org_id}/ws/${ws_id}/${analysis_id}`,
        {
          params: {
            include_results
          },
          timeout: 60000, // Longer timeout for fetching results
        }
      );

      return sanitizeResponseData(response.data) as AnalysisDetail;
    } catch (error: any) {
      console.error('Get analysis error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get analysis');
    }
  },

  // Get download URL for analysis results
  async getAnalysisDownloadUrl(
    org_id: string,
    ws_id: string,
    analysis_id: string,
    expiration: number = 7200
  ): Promise<{
    analysis_id: string;
    download_url: string;
    expires_in_seconds: number;
    file_size_mb?: string;
    filename: string;
  }> {
    try {
      const response = await axiosInstance.get(
        `/analysis-history/org/${org_id}/ws/${ws_id}/${analysis_id}/download`,
        {
          params: {
            expiration
          },
          timeout: 30000,
        }
      );

      return sanitizeResponseData(response.data);
    } catch (error: any) {
      console.error('Get download URL error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get download URL');
    }
  },

  // Delete an analysis
  async deleteAnalysis(
    org_id: string,
    ws_id: string,
    analysis_id: string
  ): Promise<{ analysis_id: string; deleted: boolean; s3_deleted: boolean }> {
    try {
      const response = await axiosInstance.delete(
        `/analysis-history/org/${org_id}/ws/${ws_id}/${analysis_id}`,
        {
          timeout: 30000,
        }
      );

      return sanitizeResponseData(response.data);
    } catch (error: any) {
      console.error('Delete analysis error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to delete analysis');
    }
  }
};
